import React, { useState, useEffect, useMemo } from 'react';
import { 
  ChevronLeft, ChevronRight, Search, Filter, MessageSquare, Clock, 
  User, MapPin, Monitor, RefreshCw, X, Send, Mail, Phone, 
  ChevronDown, ChevronUp, HardDrive, Globe, Calendar, 
  AlertCircle, CheckCircle, Clock as ClockIcon, MoreVertical,
  Download, Printer, Share2, Tag, FileText, Activity
} from 'lucide-react';
import { Chart } from 'react-google-charts';

const SupportDashboard = () => {
  // State management
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    service: '',
    dateRange: '',
    priority: ''
  });
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [response, setResponse] = useState('');
  const [stats, setStats] = useState({
    totalMessages: 0,
    pendingMessages: 0,
    resolvedMessages: 0,
    averageResponseTime: '0h',
  });
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [selectedMessages, setSelectedMessages] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch messages
  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/uss/sms?page=${currentPage}&search=${searchTerm}&status=${filters.status}&service=${filters.service}&dateRange=${filters.dateRange}&priority=${filters.priority}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch data');
      
      const data = await response.json();
      setMessages(data.messages || []);
      setTotalPages(data.total_pages || 1);
      
      setStats({
        totalMessages: data.total_messages || 0,
        pendingMessages: data.pending_messages || 0,
        resolvedMessages: data.resolved_messages || 0,
        averageResponseTime: data.average_response_time || '0h',
      });
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when filters/page changes
  useEffect(() => {
    fetchMessages();
  }, [currentPage, filters]);

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchMessages();
  };

  // Apply filters
  const applyFilters = () => {
    setCurrentPage(1);
    fetchMessages();
    setShowFilterMenu(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      status: '',
      service: '',
      dateRange: '',
      priority: ''
    });
    setCurrentPage(1);
    fetchMessages();
  };

  // Handle response submission
  const handleSubmitResponse = async () => {
    if (!response.trim()) return;
    
    try {
      const result = await fetch(`/messages/${selectedMessage.id}/respond`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ response }),
      });
      
      if (!result.ok) throw new Error('Failed to submit response');
      
      fetchMessages();
      setSelectedMessage(null);
      setResponse('');
      
    } catch (err) {
      setError(err.message);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Toggle message selection
  const toggleMessageSelect = (id) => {
    setSelectedMessages(prev => 
      prev.includes(id) 
        ? prev.filter(msgId => msgId !== id) 
        : [...prev, id]
    );
  };

  // Toggle all messages selection
  const toggleSelectAll = () => {
    if (selectedMessages.length === messages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map(msg => msg.id));
    }
  };

  // Bulk actions
  const handleBulkAction = (action) => {
    console.log(`Performing ${action} on selected messages:`, selectedMessages);
    // Implement bulk actions here
    setSelectedMessages([]);
    setShowBulkActions(false);
    fetchMessages();
  };

  // Status distribution data for chart
  const statusDistributionData = useMemo(() => [
    ['Status', 'Count'],
    ['Pending', stats.pendingMessages],
    ['Resolved', stats.resolvedMessages],
  ], [stats]);

  // Response time distribution data
  const responseTimeData = useMemo(() => [
    ['Time', 'Count'],
    ['<1h', 15],
    ['1-4h', 25],
    ['4-12h', 10],
    ['12-24h', 5],
    ['>24h', 3],
  ], []);

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h1 className="text-xl font-bold text-indigo-600">Support Dashboard</h1>
            <div className="hidden md:flex items-center space-x-2 text-sm text-gray-500">
              <span className="flex items-center">
                <Activity className="h-4 w-4 mr-1 text-indigo-500" />
                <span>Last updated: {new Date().toLocaleTimeString()}</span>
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => fetchMessages()} 
              className="p-2 rounded-full hover:bg-gray-100 transition"
              title="Refresh data"
            >
              <RefreshCw size={18} className="text-gray-600" />
            </button>
            <div className="hidden md:flex space-x-2">
              <button className="flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
                <Download size={16} className="mr-1.5" />
                Export
              </button>
              <button className="flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 hover:bg-gray-50">
                <Printer size={16} className="mr-1.5" />
                Print
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-6">
        {/* Stats and charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Stats cards */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">Overview</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-indigo-700">Total Messages</p>
                    <p className="text-2xl font-bold text-indigo-900 mt-1">{stats.totalMessages}</p>
                  </div>
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <MessageSquare size={20} className="text-indigo-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-yellow-700">Pending</p>
                    <p className="text-2xl font-bold text-yellow-900 mt-1">{stats.pendingMessages}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <AlertCircle size={20} className="text-yellow-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-green-700">Resolved</p>
                    <p className="text-2xl font-bold text-green-900 mt-1">{stats.resolvedMessages}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle size={20} className="text-green-600" />
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm font-medium text-blue-700">Avg. Response</p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">{stats.averageResponseTime}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ClockIcon size={20} className="text-blue-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Status distribution chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">Status Distribution</h2>
            <div className="h-full">
              <Chart
                chartType="PieChart"
                data={statusDistributionData}
                options={{
                  pieHole: 0.4,
                  colors: ['#F59E0B', '#10B981'],
                  backgroundColor: 'transparent',
                  legend: { position: 'bottom' },
                  chartArea: { width: '90%', height: '80%' },
                }}
                width="100%"
                height="300px"
              />
            </div>
          </div>
          
          {/* Response time chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold mb-4 text-gray-800">Response Times</h2>
            <div className="h-full">
              <Chart
                chartType="BarChart"
                data={responseTimeData}
                options={{
                  colors: ['#3B82F6'],
                  backgroundColor: 'transparent',
                  legend: { position: 'none' },
                  chartArea: { width: '85%', height: '80%' },
                  hAxis: { title: 'Count' },
                  vAxis: { title: 'Response Time' },
                }}
                width="100%"
                height="300px"
              />
            </div>
          </div>
        </div>

        {/* Search and filter */}
        <div className="flex flex-col lg:flex-row justify-between mb-6 gap-4">
          <form onSubmit={handleSearch} className="flex w-full lg:w-2/3">
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search messages, users, services..."
                className="w-full rounded-l-lg bg-white border border-gray-300 py-2.5 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Search className="absolute left-3 top-3 text-gray-400" size={18} />
            </div>
            <button 
              type="submit" 
              className="bg-indigo-600 hover:bg-indigo-700 px-4 rounded-r-lg text-white flex items-center"
            >
              <Search size={16} className="mr-1.5" />
              Search
            </button>
          </form>
          
          <div className="flex items-center space-x-3">
            <div className="relative">
              <button
                onClick={() => setShowFilterMenu(!showFilterMenu)}
                className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 px-4 py-2.5 rounded-lg"
              >
                <Filter size={16} className="text-gray-600" />
                <span className="text-gray-700">Filters</span>
                {showFilterMenu ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              
              {showFilterMenu && (
                <div className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                  <div className="p-4">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-sm font-medium text-gray-900">Filters</h3>
                      <button 
                        onClick={resetFilters}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        Reset all
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                        <select
                          className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={filters.status}
                          onChange={(e) => setFilters({...filters, status: e.target.value})}
                        >
                          <option value="">All Statuses</option>
                          <option value="Pending">Pending</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Escalated">Escalated</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Service</label>
                        <select
                          className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={filters.service}
                          onChange={(e) => setFilters({...filters, service: e.target.value})}
                        >
                          <option value="">All Services</option>
                          <option value="Account Setup">Account Setup</option>
                          <option value="Technical Support">Technical Support</option>
                          <option value="Billing">Billing</option>
                          <option value="API Support">API Support</option>
                          <option value="Feature Request">Feature Request</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Priority</label>
                        <select
                          className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={filters.priority}
                          onChange={(e) => setFilters({...filters, priority: e.target.value})}
                        >
                          <option value="">All Priorities</option>
                          <option value="Low">Low</option>
                          <option value="Medium">Medium</option>
                          <option value="High">High</option>
                          <option value="Critical">Critical</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Date Range</label>
                        <select
                          className="w-full bg-white border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={filters.dateRange}
                          onChange={(e) => setFilters({...filters, dateRange: e.target.value})}
                        >
                          <option value="">All Time</option>
                          <option value="today">Today</option>
                          <option value="yesterday">Yesterday</option>
                          <option value="week">This Week</option>
                          <option value="month">This Month</option>
                          <option value="custom">Custom Range</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                        onClick={() => setShowFilterMenu(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
                        onClick={applyFilters}
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="hidden md:flex items-center space-x-2">
              <button 
                onClick={() => setViewMode('list')} 
                className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
                title="List view"
              >
                <FileText size={16} />
              </button>
              <button 
                onClick={() => setViewMode('grid')} 
                className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}
                title="Grid view"
              >
                <Tag size={16} />
              </button>
            </div>
          </div>
        </div>

        {/* Bulk actions */}
        {selectedMessages.length > 0 && (
          <div className="mb-4 bg-indigo-50 border border-indigo-100 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm font-medium text-indigo-800 mr-3">
                {selectedMessages.length} selected
              </span>
              <button 
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="flex items-center text-sm text-indigo-700 hover:text-indigo-900"
              >
                <span>Actions</span>
                {showBulkActions ? <ChevronUp size={16} className="ml-1" /> : <ChevronDown size={16} className="ml-1" />}
              </button>
            </div>
            
            <button 
              onClick={() => setSelectedMessages([])}
              className="text-sm text-indigo-700 hover:text-indigo-900"
            >
              Clear selection
            </button>
            
            {showBulkActions && (
              <div className="absolute mt-2 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                <div className="py-1">
                  <button 
                    onClick={() => handleBulkAction('mark_pending')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Mark as Pending
                  </button>
                  <button 
                    onClick={() => handleBulkAction('mark_resolved')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Mark as Resolved
                  </button>
                  <button 
                    onClick={() => handleBulkAction('assign')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Assign to Team
                  </button>
                  <button 
                    onClick={() => handleBulkAction('export')}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Export Selected
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle size={18} className="mr-2" />
              <span>{error}</span>
            </div>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <X size={18} />
            </button>
          </div>
        )}

        {/* Messages table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 flex justify-center">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
                <p className="mt-4 text-gray-500">Loading messages...</p>
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <MessageSquare size={48} className="mx-auto mb-4 opacity-30" />
              <p>No messages found matching your criteria.</p>
              <button 
                onClick={resetFilters}
                className="mt-3 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
              >
                Clear all filters
              </button>
            </div>
          ) : viewMode === 'list' ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input 
                        type="checkbox" 
                        checked={selectedMessages.length === messages.length && messages.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {messages.map((message) => (
                    <React.Fragment key={message.id}>
                      <tr className={`hover:bg-gray-50 ${expandedMessageId === message.id ? 'bg-gray-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input 
                            type="checkbox" 
                            checked={selectedMessages.includes(message.id)}
                            onChange={() => toggleMessageSelect(message.id)}
                            className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                          />
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <MessageSquare size={16} className="text-indigo-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 line-clamp-1">
                                {message.message.substring(0, 60)}{message.message.length > 60 ? '...' : ''}
                              </div>
                              <div className="text-sm text-gray-500">{message.service.name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                              <span className="text-sm font-medium text-indigo-800">
                                {message.user.full_name.split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{message.user.full_name}</div>
                              <div className="text-sm text-gray-500">{message.user.company_name}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                            {message.service.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            message.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                            message.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                            message.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {message.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(message.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            message.priority === 'High' ? 'bg-red-100 text-red-800' :
                            message.priority === 'Medium' ? 'bg-orange-100 text-orange-800' :
                            message.priority === 'Low' ? 'bg-green-100 text-green-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {message.priority || 'Normal'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => setSelectedMessage(message)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              View
                            </button>
                            <button
                              onClick={() => setExpandedMessageId(expandedMessageId === message.id ? null : message.id)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              {expandedMessageId === message.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                      
                      {expandedMessageId === message.id && (
                        <tr className="bg-gray-50">
                          <td colSpan="8" className="px-6 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <div className="col-span-2">
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-3">Message Details</h4>
                                  <p className="text-gray-800">{message.message}</p>
                                </div>
                              </div>
                              
                              <div>
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                  <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h4>
                                  <div className="space-y-2">
                                    <button 
                                      onClick={() => setSelectedMessage(message)}
                                      className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                                    >
                                      <Send size={14} className="mr-2" />
                                      Respond
                                    </button>
                                    <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                                      <Tag size={14} className="mr-2" />
                                      Change Status
                                    </button>
                                    <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                                      <User size={14} className="mr-2" />
                                      Assign
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {messages.map((message) => (
                <div key={message.id} className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 line-clamp-2">{message.message.substring(0, 100)}{message.message.length > 100 ? '...' : ''}</h3>
                        <p className="mt-1 text-xs text-gray-500">{formatDate(message.created_at)}</p>
                      </div>
                      <div className="flex space-x-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          message.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                          message.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                          message.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {message.status}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          message.priority === 'High' ? 'bg-red-100 text-red-800' :
                          message.priority === 'Medium' ? 'bg-orange-100 text-orange-800' :
                          message.priority === 'Low' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {message.priority || 'Normal'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mt-4 flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 bg-indigo-100 rounded-full flex items-center justify-center">
                        <span className="text-xs font-medium text-indigo-800">
                          {message.user.full_name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900">{message.user.full_name}</p>
                        <p className="text-xs text-gray-500">{message.user.company_name}</p>
                      </div>
                    </div>
                    
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-medium text-gray-500">{message.service.name}</span>
                        <button
                          onClick={() => setSelectedMessage(message)}
                          className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                        >
                          View details
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Showing <span className="font-medium">{(currentPage - 1) * 10 + 1}</span> to{' '}
            <span className="font-medium">{Math.min(currentPage * 10, stats.totalMessages)}</span> of{' '}
            <span className="font-medium">{stats.totalMessages}</span> results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className={`px-3 py-1.5 border rounded-md text-sm font-medium ${
                currentPage === 1 
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <div className="flex space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-1.5 border rounded-md text-sm font-medium ${
                      currentPage === pageNum
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              {totalPages > 5 && (
                <>
                  <span className="px-3 py-1.5 text-sm text-gray-500">...</span>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    className={`px-3 py-1.5 border rounded-md text-sm font-medium ${
                      currentPage === totalPages
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-600'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {totalPages}
                  </button>
                </>
              )}
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className={`px-3 py-1.5 border rounded-md text-sm font-medium ${
                currentPage === totalPages 
                  ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
        </div>
      </main>

      {/* Message detail modal */}
      {selectedMessage && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Ticket #{selectedMessage.id}</h3>
                <p className="text-sm text-gray-500">{selectedMessage.service.name}</p>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedMessage.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' :
                  selectedMessage.status === 'Resolved' ? 'bg-green-100 text-green-800' :
                  selectedMessage.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {selectedMessage.status}
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedMessage.priority === 'High' ? 'bg-red-100 text-red-800' :
                  selectedMessage.priority === 'Medium' ? 'bg-orange-100 text-orange-800' :
                  selectedMessage.priority === 'Low' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {selectedMessage.priority || 'Normal'}
                </span>
                <button 
                  onClick={() => setSelectedMessage(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-indigo-800">
                          {selectedMessage.user.full_name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center">
                          <p className="font-medium text-gray-900">{selectedMessage.user.full_name}</p>
                          <span className="mx-2 text-gray-300">•</span>
                          <p className="text-sm text-gray-500">{formatDate(selectedMessage.created_at)}</p>
                        </div>
                        <p className="mt-2 text-gray-700 whitespace-pre-line">{selectedMessage.message}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Response</h4>
                    <textarea
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                      placeholder="Type your response here..."
                      className="w-full border border-gray-300 rounded-lg p-3 text-gray-700 min-h-32 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      rows={5}
                    ></textarea>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">User Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center mb-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                          <User size={16} className="text-indigo-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{selectedMessage.user.full_name}</p>
                          <p className="text-sm text-gray-500">{selectedMessage.user.company_name}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center text-sm text-gray-700">
                          <Mail size={14} className="mr-2 text-gray-400" />
                          <span>{selectedMessage.user.email}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-700">
                          <Phone size={14} className="mr-2 text-gray-400" />
                          <span>{selectedMessage.user.phone || 'Not provided'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">System Information</h4>
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center text-sm text-gray-700">
                        <Monitor size={14} className="mr-2 text-gray-400" />
                        <span>{selectedMessage.browser} {selectedMessage.browser_version}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <HardDrive size={14} className="mr-2 text-gray-400" />
                        <span>{selectedMessage.os} {selectedMessage.os_version}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <Globe size={14} className="mr-2 text-gray-400" />
                        <span>{selectedMessage.ip_address}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <MapPin size={14} className="mr-2 text-gray-400" />
                        <span>
                          {selectedMessage.location.city}, {selectedMessage.location.region}, {selectedMessage.location.country}
                        </span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <Calendar size={14} className="mr-2 text-gray-400" />
                        <span>Created: {formatDate(selectedMessage.created_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
                    <div className="space-y-2">
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <Tag size={14} className="mr-2" />
                        Change Status
                      </button>
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <User size={14} className="mr-2" />
                        Assign to Team Member
                      </button>
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <ClockIcon size={14} className="mr-2" />
                        Set Reminder
                      </button>
                      <button className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                        <FileText size={14} className="mr-2" />
                        View History
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 border-t border-gray-200 flex justify-between">
              <div className="flex items-center space-x-3">
                <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Save Draft
                </button>
                <button className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Add Note
                </button>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setSelectedMessage(null)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitResponse}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-md text-sm font-medium text-white flex items-center"
                  disabled={!response.trim()}
                >
                  <Send size={14} className="mr-2" />
                  Send Response
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportDashboard;